import type { Data } from '../types';
import MatchCard from './MatchCard';
import UploadCard from './UploadCard';

type HeroProps = {
  mode: 'example' | 'upload';
  onModeChange: (mode: 'example' | 'upload') => void;
  isUploading: boolean;
  uploadError: string | null;
  uploadResult: Data | null;
  uploadFooter: string;
  onFileSelected: (file: File) => void;
  selectedFileName: string;
};

export default function Hero({
  mode,
  onModeChange,
  isUploading,
  uploadError,
  uploadResult,
  uploadFooter,
  onFileSelected,
  selectedFileName,
}: HeroProps) {
  const isExample = mode === 'example';
  const exampleStatus = uploadError
    ? 'Example match failed to load.'
    : isUploading
      ? `Analyzing ${selectedFileName || 'example.txt'}...`
      : 'Preparing example match data.';

  return (
    <section className="grid grid-cols-1 items-stretch gap-8 min-[960px]:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
      <div className="animate-rise">
        <p className="text-[12px] font-semibold uppercase tracking-[2px] text-accent">
          Upload or explore
        </p>
        <h1 className="mt-3 font-display text-[clamp(2.4rem,3vw,3.4rem)] tracking-[1px]">
          Match logs, visualized.
        </h1>
        <p className="mt-3 max-w-[520px] text-[16px] leading-relaxed text-muted">
          Upload a CS:GO match log file and get flash effectiveness, basic stats, damage heatmaps.
        </p>
        <div className="mt-5 inline-flex gap-1.5 rounded-full border border-stroke bg-card p-1.5 shadow-[0_12px_24px_rgba(0,0,0,0.35)]">
          <button
            className={`rounded-full px-[18px] py-[10px] text-[13px] font-semibold transition-all ${
              isExample
                ? 'bg-accent text-bg shadow-[0_10px_22px_rgba(255,254,62,0.35)]'
                : 'text-muted hover:text-ink'
            }`}
            type="button"
            onClick={() => onModeChange('example')}
            aria-pressed={isExample}
          >
            Example match
          </button>
          <button
            className={`rounded-full px-[18px] py-[10px] text-[13px] font-semibold transition-all ${
              !isExample
                ? 'bg-accent text-bg shadow-[0_10px_22px_rgba(255,254,62,0.35)]'
                : 'text-muted hover:text-ink'
            }`}
            type="button"
            onClick={() => onModeChange('upload')}
            aria-pressed={!isExample}
          >
            Upload your match
          </button>
        </div>
      </div>

      <div className="flex animate-rise flex-col gap-5" style={{ animationDelay: '120ms' }}>
        {mode === 'upload' && !uploadResult && (
          <UploadCard
            isUploading={isUploading}
            uploadError={uploadError}
            uploadResult={uploadResult}
            uploadFooter={uploadFooter}
            onFileSelected={onFileSelected}
          />
        )}
        {mode === 'example' && !uploadResult && (
          <div className="rounded-[20px] border border-accent/35 bg-[linear-gradient(180deg,rgba(53,25,44,0.95),rgba(31,13,26,0.95))] p-5 shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[18px] font-bold">Example match</div>
                <div className="mt-1 text-[13px] text-muted">{exampleStatus}</div>
              </div>
            </div>
            {uploadError && (
              <div className="mt-4 rounded-xl border border-t/40 bg-t/10 px-3 py-2.5 text-[12px] text-t">
                {uploadError}
              </div>
            )}
          </div>
        )}
        {uploadResult && <MatchCard data={uploadResult} />}
      </div>
    </section>
  );
}
